export interface GuestBlogSite {
  id: string;
  site_url: string;
  da: number; // Domain Authority
  dr: number; // Domain Rating
  ahrefs_traffic: number;
  ss?: number; // Spam Score (optional)
  tat: string; // Turnaround Time
  category: GuestBlogSiteCategory;
  categoryDisplay: string;
  status: GuestBlogSiteStatus;
  negotiation_status: NegotiationStatus;
  base_price: number; // GBS Base price (internal)
  li_base_price?: number; // LI Base price (internal, optional)
  displayed_price: number; // GBS Price - What users see in table (base_price + client percentage)
  li_displayed_price?: number; // LI Price - What users see in table (li_base_price + client percentage)
  country: string;
  outreach_email?: string;
  publisher_id: string;
  site_language: string;
  createdAt: string;
  updatedAt: string;
  publisher?: {
    id: string;
    publisherName: string;
    email?: string;
  };
  isOverride?: boolean;
  // Site URL checking status fields
  siteStatusCode?: number;
  siteLastChecked?: string;
  siteCheckStatus?: string;
}

export enum GuestBlogSiteCategory {
  AI_FUTURE_TECH = 'AI_FUTURE_TECH',
  AUTOMOBILE = 'AUTOMOBILE',
  BUSINESS_ENTREPRENEURSHIP = 'BUSINESS_ENTREPRENEURSHIP',
  CASINO = 'CASINO',
  ECOMMERCE_STARTUPS = 'ECOMMERCE_STARTUPS',
  EDUCATION_CAREER = 'EDUCATION_CAREER',
  ENTERTAINMENT_MEDIA = 'ENTERTAINMENT_MEDIA',
  ENVIRONMENT = 'ENVIRONMENT',
  ESPORTS = 'ESPORTS',
  FAMILY = 'FAMILY',
  FASHION_BEAUTY = 'FASHION_BEAUTY',
  FINANCE_INVESTMENT = 'FINANCE_INVESTMENT',
  FOOD_NUTRITION = 'FOOD_NUTRITION',
  GAMING = 'GAMING',
  GENERAL = 'GENERAL',
  HEALTH_FITNESS = 'HEALTH_FITNESS',
  HOME_IMPROVEMENT = 'HOME_IMPROVEMENT',
  LAW = 'LAW',
  LIFESTYLE_WELLNESS = 'LIFESTYLE_WELLNESS',
  MAGAZINE = 'MAGAZINE',
  MARKETING_SEO = 'MARKETING_SEO',
  NEWS = 'NEWS',
  OTHERS = 'OTHERS',
  PARENTING_RELATIONSHIPS = 'PARENTING_RELATIONSHIPS',
  REAL_ESTATE = 'REAL_ESTATE',
  SPORTS_FITNESS = 'SPORTS_FITNESS',
  SUSTAINABILITY_GREEN_LIVING = 'SUSTAINABILITY_GREEN_LIVING',
  TECHNOLOGY_GADGETS = 'TECHNOLOGY_GADGETS',
  TRAVEL_TOURISM = 'TRAVEL_TOURISM',
}

export enum GuestBlogSiteStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum NegotiationStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
}

export enum SubmissionType {
  GBS = 'GBS',   // Guest Blog Site Submission
  LI = 'LI',     // Link Insertion Submission
  BOTH = 'BOTH', // Both GBS and LI
}

export interface CreateGuestBlogSiteRequest {
  site_url: string;
  da: number;
  dr: number;
  ahrefs_traffic: number;
  ss?: number;
  tat: string;
  category: GuestBlogSiteCategory;
  status?: GuestBlogSiteStatus;
  negotiation_status?: NegotiationStatus;
  base_price: number;
  li_base_price?: number | null;
  country: string;
  outreach_email?: string;
  publisher_id: string;
  site_language: string;
}

export interface UpdateGuestBlogSiteRequest {
  da?: number;
  dr?: number;
  ahrefs_traffic?: number;
  ss?: number;
  tat?: string;
  category?: GuestBlogSiteCategory;
  status?: GuestBlogSiteStatus;
  negotiation_status?: NegotiationStatus;
  base_price?: number;
  li_base_price?: number | null;
  country?: string;
  outreach_email?: string;
  publisher_id?: string;
  site_language: string;
}

export interface GuestBlogSiteFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  country?: string;
  siteLanguage?: string;
  status?: GuestBlogSiteStatus;
  publisherId?: string;
  outreachEmail?: string;
  clientId?: string;
  projectId?: string;
  planId?: string;
  includeUsageStats?: string;
  minDr?: number;
  maxDr?: number;
  minTraffic?: number;
  maxTraffic?: number;
  minPrice?: number;
  maxPrice?: number;
  fromDate?: string;
  toDate?: string;
  sortBy?: string;
  sortOrder?: string;
  // Cache-busting parameter
  _?: number;
}

export interface GuestBlogSitePricing {
  siteId: string;
  basePrice: number;
  clientPercentage: number;
  finalPrice: number;
  isOverride: boolean;
  overridePrice?: number;
}

export interface SetPriceOverrideRequest {
  overridePrice?: number;
  gbOverridePrice?: number;
  liOverridePrice?: number;
}

// Category options for dropdowns
export const GUEST_BLOG_SITE_CATEGORIES = [
  { value: GuestBlogSiteCategory.AI_FUTURE_TECH, label: 'AI & Future Tech' },
  { value: GuestBlogSiteCategory.AUTOMOBILE, label: 'Automobile' },
  { value: GuestBlogSiteCategory.BUSINESS_ENTREPRENEURSHIP, label: 'Business & Entrepreneurship' },
  { value: GuestBlogSiteCategory.CASINO, label: 'Casino' },
  { value: GuestBlogSiteCategory.ECOMMERCE_STARTUPS, label: 'E-commerce & Startups' },
  { value: GuestBlogSiteCategory.EDUCATION_CAREER, label: 'Education & Career' },
  { value: GuestBlogSiteCategory.ENTERTAINMENT_MEDIA, label: 'Entertainment & Media' },
  { value: GuestBlogSiteCategory.ENVIRONMENT, label: 'Environment' },
  { value: GuestBlogSiteCategory.ESPORTS, label: 'Esports' },
  { value: GuestBlogSiteCategory.FAMILY, label: 'Family' },
  { value: GuestBlogSiteCategory.FASHION_BEAUTY, label: 'Fashion & Beauty' },
  { value: GuestBlogSiteCategory.FINANCE_INVESTMENT, label: 'Finance & Investment' },
  { value: GuestBlogSiteCategory.FOOD_NUTRITION, label: 'Food & Nutrition' },
  { value: GuestBlogSiteCategory.GAMING, label: 'Gaming' },
  { value: GuestBlogSiteCategory.GENERAL, label: 'General' },
  { value: GuestBlogSiteCategory.HEALTH_FITNESS, label: 'Health & Fitness' },
  { value: GuestBlogSiteCategory.HOME_IMPROVEMENT, label: 'Home Improvement' },
  { value: GuestBlogSiteCategory.LAW, label: 'Law' },
  { value: GuestBlogSiteCategory.LIFESTYLE_WELLNESS, label: 'Lifestyle & Wellness' },
  { value: GuestBlogSiteCategory.MAGAZINE, label: 'Magazine' },
  { value: GuestBlogSiteCategory.MARKETING_SEO, label: 'Marketing & SEO' },
  { value: GuestBlogSiteCategory.NEWS, label: 'News' },
  { value: GuestBlogSiteCategory.OTHERS, label: 'Others' },
  { value: GuestBlogSiteCategory.PARENTING_RELATIONSHIPS, label: 'Parenting & Relationships' },
  { value: GuestBlogSiteCategory.REAL_ESTATE, label: 'Real Estate' },
  { value: GuestBlogSiteCategory.SPORTS_FITNESS, label: 'Sports & Fitness' },
  { value: GuestBlogSiteCategory.SUSTAINABILITY_GREEN_LIVING, label: 'Sustainability & Green Living' },
  { value: GuestBlogSiteCategory.TECHNOLOGY_GADGETS, label: 'Technology & Gadgets' },
  { value: GuestBlogSiteCategory.TRAVEL_TOURISM, label: 'Travel & Tourism' },
];

// Language options for dropdowns
export const SITE_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
  { value: 'bn', label: 'Bengali' },
  { value: 'ur', label: 'Urdu' },
  { value: 'id', label: 'Indonesian' },
  { value: 'ms', label: 'Malay' },
  { value: 'th', label: 'Thai' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'tr', label: 'Turkish' },
  { value: 'pl', label: 'Polish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'sv', label: 'Swedish' },
  { value: 'da', label: 'Danish' },
  { value: 'no', label: 'Norwegian' },
  { value: 'fi', label: 'Finnish' },
  { value: 'cs', label: 'Czech' },
  { value: 'sk', label: 'Slovak' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'ro', label: 'Romanian' },
  { value: 'bg', label: 'Bulgarian' },
  { value: 'hr', label: 'Croatian' },
  { value: 'sr', label: 'Serbian' },
  { value: 'sl', label: 'Slovenian' },
  { value: 'et', label: 'Estonian' },
  { value: 'lv', label: 'Latvian' },
  { value: 'lt', label: 'Lithuanian' },
  { value: 'mt', label: 'Maltese' },
  { value: 'ga', label: 'Irish' },
  { value: 'cy', label: 'Welsh' },
  { value: 'is', label: 'Icelandic' },
  { value: 'mk', label: 'Macedonian' },
  { value: 'sq', label: 'Albanian' },
  { value: 'eu', label: 'Basque' },
  { value: 'ca', label: 'Catalan' },
  { value: 'gl', label: 'Galician' },
];

// Country options for dropdowns
export const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'ES', label: 'Spain' },
  { value: 'IT', label: 'Italy' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'IE', label: 'Ireland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'GR', label: 'Greece' },
  { value: 'PL', label: 'Poland' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'HU', label: 'Hungary' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'HR', label: 'Croatia' },
  { value: 'RO', label: 'Romania' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'EE', label: 'Estonia' },
  { value: 'LV', label: 'Latvia' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'MT', label: 'Malta' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'IS', label: 'Iceland' },
  { value: 'LI', label: 'Liechtenstein' },
  { value: 'MC', label: 'Monaco' },
  { value: 'SM', label: 'San Marino' },
  { value: 'VA', label: 'Vatican City' },
  { value: 'AD', label: 'Andorra' },
  { value: 'RU', label: 'Russia' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'BY', label: 'Belarus' },
  { value: 'MD', label: 'Moldova' },
  { value: 'RS', label: 'Serbia' },
  { value: 'ME', label: 'Montenegro' },
  { value: 'BA', label: 'Bosnia and Herzegovina' },
  { value: 'MK', label: 'North Macedonia' },
  { value: 'AL', label: 'Albania' },
  { value: 'XK', label: 'Kosovo' },
  { value: 'TR', label: 'Turkey' },
  { value: 'JP', label: 'Japan' },
  { value: 'KR', label: 'South Korea' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'India' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'NP', label: 'Nepal' },
  { value: 'BT', label: 'Bhutan' },
  { value: 'MV', label: 'Maldives' },
  { value: 'AF', label: 'Afghanistan' },
  { value: 'IR', label: 'Iran' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'QA', label: 'Qatar' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'OM', label: 'Oman' },
  { value: 'YE', label: 'Yemen' },
  { value: 'JO', label: 'Jordan' },
  { value: 'LB', label: 'Lebanon' },
  { value: 'SY', label: 'Syria' },
  { value: 'IL', label: 'Israel' },
  { value: 'PS', label: 'Palestine' },
  { value: 'EG', label: 'Egypt' },
  { value: 'LY', label: 'Libya' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'MA', label: 'Morocco' },
  { value: 'SD', label: 'Sudan' },
  { value: 'SS', label: 'South Sudan' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'ER', label: 'Eritrea' },
  { value: 'DJ', label: 'Djibouti' },
  { value: 'SO', label: 'Somalia' },
  { value: 'KE', label: 'Kenya' },
  { value: 'UG', label: 'Uganda' },
  { value: 'TZ', label: 'Tanzania' },
  { value: 'RW', label: 'Rwanda' },
  { value: 'BI', label: 'Burundi' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'MW', label: 'Malawi' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },
  { value: 'BW', label: 'Botswana' },
  { value: 'NA', label: 'Namibia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'LS', label: 'Lesotho' },
  { value: 'SZ', label: 'Eswatini' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'SC', label: 'Seychelles' },
  { value: 'KM', label: 'Comoros' },
  { value: 'BR', label: 'Brazil' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'PE', label: 'Peru' },
  { value: 'CO', label: 'Colombia' },
  { value: 'VE', label: 'Venezuela' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'GY', label: 'Guyana' },
  { value: 'SR', label: 'Suriname' },
  { value: 'GF', label: 'French Guiana' },
  { value: 'FK', label: 'Falkland Islands' },
  { value: 'MX', label: 'Mexico' },
  { value: 'GT', label: 'Guatemala' },
  { value: 'BZ', label: 'Belize' },
  { value: 'SV', label: 'El Salvador' },
  { value: 'HN', label: 'Honduras' },
  { value: 'NI', label: 'Nicaragua' },
  { value: 'CR', label: 'Costa Rica' },
  { value: 'PA', label: 'Panama' },
  { value: 'CU', label: 'Cuba' },
  { value: 'JM', label: 'Jamaica' },
  { value: 'HT', label: 'Haiti' },
  { value: 'DO', label: 'Dominican Republic' },
  { value: 'PR', label: 'Puerto Rico' },
  { value: 'TT', label: 'Trinidad and Tobago' },
  { value: 'BB', label: 'Barbados' },
  { value: 'GD', label: 'Grenada' },
  { value: 'LC', label: 'Saint Lucia' },
  { value: 'VC', label: 'Saint Vincent and the Grenadines' },
  { value: 'AG', label: 'Antigua and Barbuda' },
  { value: 'KN', label: 'Saint Kitts and Nevis' },
  { value: 'DM', label: 'Dominica' },
  { value: 'BS', label: 'Bahamas' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'FJ', label: 'Fiji' },
  { value: 'PG', label: 'Papua New Guinea' },
  { value: 'SB', label: 'Solomon Islands' },
  { value: 'VU', label: 'Vanuatu' },
  { value: 'NC', label: 'New Caledonia' },
  { value: 'PF', label: 'French Polynesia' },
  { value: 'WS', label: 'Samoa' },
  { value: 'TO', label: 'Tonga' },
  { value: 'KI', label: 'Kiribati' },
  { value: 'TV', label: 'Tuvalu' },
  { value: 'NR', label: 'Nauru' },
  { value: 'PW', label: 'Palau' },
  { value: 'FM', label: 'Micronesia' },
  { value: 'MH', label: 'Marshall Islands' },
];

// Negotiation Status options for dropdowns
export const NEGOTIATION_STATUS_OPTIONS = [
  { value: NegotiationStatus.IN_PROGRESS, label: 'In Progress' },
  { value: NegotiationStatus.DONE, label: 'Done' },
];

// Submission Type options for dropdowns
export const SUBMISSION_TYPE_OPTIONS = [
  { value: SubmissionType.GBS, label: 'GBS (Guest Blog Site) Submission' },
  { value: SubmissionType.LI, label: 'LI (Link Insertion) Submission' },
  { value: SubmissionType.BOTH, label: 'Both GBS and LI' },
];

/** HTTP codes where the server responded but blocked automated requests — site is still live. */
const ANTI_BOT_STATUS_CODES = new Set([401, 403, 405, 406, 429]);

/** Whether a guest blog site URL should show as healthy in the UI. */
export function isGuestBlogSiteHealthOk(site: Pick<GuestBlogSite, 'siteCheckStatus' | 'siteStatusCode'>): boolean {
  if (site.siteCheckStatus === 'OK') return true;
  if (site.siteCheckStatus === 'ERROR' || site.siteCheckStatus === 'TIMEOUT') return false;
  const code = site.siteStatusCode;
  if (code == null) return false;
  if (code >= 200 && code < 400) return true;
  return ANTI_BOT_STATUS_CODES.has(code);
}

export function guestBlogSiteHealthTooltip(site: Pick<GuestBlogSite, 'siteCheckStatus' | 'siteStatusCode' | 'siteLastChecked'>): string {
  const code = site.siteStatusCode;
  const checked = site.siteLastChecked ? new Date(site.siteLastChecked).toLocaleString() : 'Never';
  const status = site.siteCheckStatus || 'Unknown';
  if (code != null && ANTI_BOT_STATUS_CODES.has(code) && isGuestBlogSiteHealthOk(site)) {
    return `HTTP ${code} — site blocks bots but is reachable · ${status} · Last checked: ${checked}`;
  }
  return `Status: ${code ?? 'N/A'} ${status} · Last checked: ${checked}`;
}
